import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../AppClient";
import { Container, Form, Button, Table, Alert, Spinner, Modal, Pagination, Card, Row, Col } from "react-bootstrap";
import { FERTILIZER_UNIT } from "../../constants/Constants";
import { FaSearch, FaEdit, FaTrash, FaPlus } from "react-icons/fa";

const Fertilizers = () => {
  const [fertilizers, setFertilizers] = useState([]);
  const [filteredFertilizers, setFilteredFertilizers] = useState([]);
  const [newFertilizerName, setNewFertilizerName] = useState("");
  const [newFertilizerAmount, setNewFertilizerAmount] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFertilizer, setSelectedFertilizer] = useState({ id: null, name: "", amount: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 5;

  const nameRef = useRef(null);
  const amountRef = useRef(null);

  // Fetch fertilizers
  const fetchFertilizers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/fertilizers/");
      setFertilizers(response.data);
      setFilteredFertilizers(response.data);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في تحميل الأسمدة.");
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  useEffect(() => {
    const filtered = fertilizers.filter(fertilizer =>
      fertilizer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fertilizer.amount.toString().includes(searchTerm)
    );
    setFilteredFertilizers(filtered);
    setCurrentPage(1);
  }, [searchTerm, fertilizers]);

  // Add fertilizer
  const addFertilizer = async () => {
    const name = nameRef.current.value;
    const amount = amountRef.current.value;

    if (!name || !amount) {
      return setError("الرجاء إدخال اسم السماد والكمية");
    }

    try {
      await apiClient.post("/fertilizers/create/", { name, amount: parseFloat(amount) });
      fetchFertilizers();
      setNewFertilizerName("");
      setNewFertilizerAmount("");
      nameRef.current.value = "";
      amountRef.current.value = "";
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في إضافة السماد.");
    }
  };

  const openEditModal = (fertilizer) => {
    setSelectedFertilizer({ id: fertilizer.id, name: fertilizer.name, amount: fertilizer.amount });
    setShowEditModal(true);
  };

  const saveEditedFertilizer = async () => {
    if (!selectedFertilizer.name || !selectedFertilizer.amount) {
      return setError("الرجاء إدخال اسم السماد والكمية");
    }

    try {
      await apiClient.put(`/fertilizers/${selectedFertilizer.id}/update/`, {
        name: selectedFertilizer.name,
        amount: parseFloat(selectedFertilizer.amount),
      });
      fetchFertilizers();
      setShowEditModal(false);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في تعديل السماد.");
    }
  };

  const deleteFertilizer = async (id) => {
    const confirmDelete = window.confirm("هل أنت متأكد أنك تريد حذف هذا السماد؟");
    if (!confirmDelete) return;

    try {
      await apiClient.delete(`/fertilizers/${id}/delete/`);
      fetchFertilizers();
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في حذف السماد.");
    }
  };

  useEffect(() => {
    fetchFertilizers();
  }, []);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredFertilizers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFertilizers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const renderPagination = () => {
    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
          style={{
            backgroundColor: number === currentPage ? "darkgreen" : "white",
            color: number === currentPage ? "white" : "darkgreen",
            borderColor: "darkgreen"
          }}
        >
          {number}
        </Pagination.Item>
      );
    }
    return <Pagination className="justify-content-center">{items}</Pagination>;
  };

  return (
    <Container fluid className="py-4 px-md-4" style={{ direction: "rtl", textAlign: "right", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      <Row className="mb-4">
        <Col>
          <h1 style={{ color: "darkgreen", fontWeight: "600", fontSize: "2.5rem" }}>الأسمدة</h1>
          <p className="text-muted">إدارة مخزون الأسمدة والمواد الكيميائية</p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={8} lg={6} xl={5}>
          {/* Search Bar */}
          <div className="mb-3">
            <div className="input-group shadow-sm">
              <input
                type="text"
                className="form-control border-end-0"
                placeholder="ابحث عن سماد..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ borderColor: "#28a745", padding: "10px 15px" }}
              />
              <span 
                className="input-group-text border-start-0" 
                style={{ backgroundColor: "#28a745", color: "white" }}
              >
                <FaSearch />
              </span>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={5} className="mb-4 mb-lg-0">
          <Card className="shadow-sm border-0" style={{ borderRadius: "12px" }}>
            <Card.Header style={{ backgroundColor: "#28a745", color: "white", borderRadius: "12px 12px 0 0" }}>
              <h5 className="mb-0 py-2">إضافة سماد جديد</h5>
            </Card.Header>
            <Card.Body className="px-4 py-4">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>اسم السماد</Form.Label>
                  <Form.Control
                    ref={nameRef}
                    type="text"
                    placeholder="أدخل اسم السماد"
                    value={newFertilizerName}
                    onChange={(e) => setNewFertilizerName(e.target.value)}
                    className="py-2"
                    style={{ borderRadius: "8px", borderColor: "#28a745" }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>الكمية ({FERTILIZER_UNIT})</Form.Label>
                  <Form.Control
                    ref={amountRef}
                    type="number"
                    placeholder="أدخل كمية السماد"
                    value={newFertilizerAmount}
                    onChange={(e) => setNewFertilizerAmount(e.target.value)}
                    className="py-2"
                    style={{ borderRadius: "8px", textAlign: "right", borderColor: "#28a745" }}
                  />
                </Form.Group>
                <Button 
                  onClick={addFertilizer} 
                  className="w-100 d-flex align-items-center justify-content-center"
                  style={{ 
                    borderRadius: "8px", 
                    padding: "10px",
                    backgroundColor: "#28a745",
                    borderColor: "#28a745",
                    transition: "all 0.3s ease"
                  }}
                >
                  <FaPlus className="me-2" /> إضافة سماد
                </Button>
              </Form>
            </Card.Body>
          </Card>
          
          {/* Error Message */}
          {error && (
            <Alert variant="danger" className="mt-3 shadow-sm" style={{ borderRadius: "8px" }}>
              {error}
            </Alert>
          )}
        </Col>
        
        <Col lg={7}>
          <Card className="shadow-sm border-0" style={{ borderRadius: "12px" }}>
            <Card.Header style={{ backgroundColor: "#28a745", color: "white", borderRadius: "12px 12px 0 0" }}>
              <h5 className="mb-0 py-2">قائمة الأسمدة</h5>
            </Card.Header>
            <Card.Body className="p-0">
              {/* Loading Spinner */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status" style={{ color: "#28a745" }}>
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <>
                  {/* Fertilizers Table */}
                  <Table hover responsive className="mb-0">
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th className="py-3">#</th>
                        <th className="py-3">اسم السماد</th>
                        <th className="py-3">الكمية ({FERTILIZER_UNIT})</th>
                        <th className="py-3">الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">لا توجد أسمدة حاليا</td>
                        </tr>
                      ) : (
                        currentItems.map((fertilizer, index) => (
                          <tr key={fertilizer.id}>
                            <td className="py-3">{indexOfFirstItem + index + 1}</td>
                            <td className="py-3">{fertilizer.name}</td>
                            <td className="py-3">{fertilizer.amount}</td>
                            <td className="py-3">
                              <Button
                                size="sm"
                                variant="success"
                                className="me-2"
                                onClick={() => openEditModal(fertilizer)}
                              >
                                <FaEdit className="me-1" /> تعديل
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => deleteFertilizer(fertilizer.id)}
                              >
                                <FaTrash className="me-1" /> حذف
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {filteredFertilizers.length > itemsPerPage && (
                    <div className="py-3 px-3 border-top">
                      <Pagination className="justify-content-center mb-0">
                        <Pagination.Prev
                          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        />
                        {Array.from({ length: totalPages }, (_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => handlePageChange(i + 1)}
                            style={{
                              backgroundColor: i + 1 === currentPage ? "#28a745" : "white",
                              borderColor: "#28a745"
                            }}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#f8f9fa", border: "none" }}>
          <Modal.Title style={{ color: "#28a745" }}>تعديل السماد</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>اسم السماد</Form.Label>
              <Form.Control
                type="text"
                value={selectedFertilizer.name}
                onChange={(e) => setSelectedFertilizer({ ...selectedFertilizer, name: e.target.value })}
                className="py-2"
                style={{ borderRadius: "8px", borderColor: "#28a745" }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>الكمية ({FERTILIZER_UNIT})</Form.Label>
              <Form.Control
                type="number"
                value={selectedFertilizer.amount}
                onChange={(e) => setSelectedFertilizer({ ...selectedFertilizer, amount: e.target.value })}
                className="py-2"
                style={{ borderRadius: "8px", borderColor: "#28a745" }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ border: "none" }}>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowEditModal(false)}
            style={{ borderRadius: "8px" }}
          >
            إغلاق
          </Button>
          <Button 
            variant="success"
            onClick={saveEditedFertilizer}
            style={{ borderRadius: "8px" }}
          >
            حفظ التعديلات
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Fertilizers;